import { ChakraProvider, createSystem, defaultConfig, defineConfig, defineRecipe, defineSlotRecipe } from "@chakra-ui/react";
import { tableAnatomy } from "@chakra-ui/react/anatomy";
import type { ReactNode } from "react";
// Shipped with the app, not assumed. Chakra's theme asked for Inter, which
// nothing loaded, so the text was in whatever each computer had -- with
// weights differing between browsers. These are CeMM's own typefaces, from
// its colour and font chart: Space Grotesk for titles, Source Sans for
// running text. src/fonts.css says which scripts are included.
import "../../fonts.css";
import { Toaster } from "./toaster";
import { cemmColors, cemmSemanticColors } from "./palette";

// Chakra's own fallback stack, behind the bundled font.
const FALLBACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;

/**
 * Chakra's default theme, with the few things the app decides once rather than
 * page by page.
 *
 * Table column headers: small, spaced capitals in the muted colour -- the same
 * voice as the card and section labels, so a header reads as a label for the
 * column rather than as the first row of data. Set here so every table gets it,
 * including the ones not written yet; a header that needs otherwise (the
 * programme matrix's rotated activity titles) still says so on itself.
 */
const config = defineConfig({
  // Chakra's reset gives every element `font: inherit`, which takes the
  // browser's italics from <em> and <i>; it puts bold back on <b> and
  // <strong>, but not italics. Put back here, so emphasis written as HTML is
  // emphasis. `fontStyle` stays the way to set italics that are not emphasis.
  globalCss: {
    "em, i": { fontStyle: "italic" },
  },
  theme: {
    tokens: {
      // CeMM's colours, into Chakra's own scales: see palette.ts.
      colors: cemmColors,
      fonts: {
        // Titles only: the Heading component uses it, labels, table
        // headers and badges stay in the body font.
        heading: { value: `"Space Grotesk Variable", ${FALLBACK}` },
        body: { value: `"Source Sans 3 Variable", ${FALLBACK}` },
      },
    },
    semanticTokens: {
      colors: cemmSemanticColors,
    },
    recipes: {
      // The chart's weight for titles is Medium; Chakra's default is
      // semibold, which in Space Grotesk reads heavy.
      heading: defineRecipe({ base: { fontWeight: "medium" } }),
      // One badge style for the whole app, chosen from side-by-side
      // variants: small spaced capitals at medium weight, with room around
      // them. Pages say only what a badge means -- its colour and whether it
      // is filled or an outline -- never how it is set. Tags are outline in
      // their own colour; statuses are filled in the status colours.
      badge: defineRecipe({
        base: {
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: "medium",
        },
        variants: {
          size: {
            xs: { px: "2", py: "1" },
          },
        },
        defaultVariants: { size: "xs" },
      }),
    },
    slotRecipes: {
      // Merged into Chakra's own table recipe, not a replacement for it: only
      // what is named here changes.
      table: defineSlotRecipe({
        slots: tableAnatomy.keys(),
        base: {
          columnHeader: {
            fontSize: "xs",
            fontWeight: "medium",
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: "fg.muted",
          },
        },
      }),
    },
  },
});

const system = createSystem(defaultConfig, config);

export function Provider({ children }: { children: ReactNode }) {
  return (
    <ChakraProvider value={system}>
      {children}
      {/* Mounted once, above every page: a message about something that just
          happened should not depend on which page is open, nor scroll away
          with it. */}
      <Toaster />
    </ChakraProvider>
  );
}
